/**
 * OU POSER LA PILULE DE REACTIONS d'un message : une fonction pure, sur des
 * rectangles deja mesures (le composant mesure, cette regle decide).
 *
 * La pilule se pose AU-DESSUS de la bulle de son message, comme sur WhatsApp :
 * son bas a `ECART_DU_CHOIX` du haut de la bulle. Elle peut deborder sur le
 * message du dessus, jamais couvrir le sien. Sans la place au-dessus (le
 * premier message, colle en haut du panneau), elle passe EN DESSOUS de la
 * bulle. Horizontalement elle s'aligne sur le bord de la bulle du cote de
 * l'auteur (gauche pour un message recu, droite pour le sien). Dans les deux
 * sens elle est BORNEE dans la zone visible, avec une marge : le panneau qui
 * rogne ne coupe jamais un emoji.
 */

/** Un rectangle en coordonnees de la fenetre (ce que rend `getBoundingClientRect`). */
export interface RectMesure {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/** La place voulue par le choix, en pixels. */
export interface TailleDuChoix {
  largeur: number;
  hauteur: number;
}

/** Le jeu de marge entre le choix et le bord de la zone visible. */
export const MARGE_DU_CHOIX = 8;
/** L'air entre la pilule et la bulle (au-dessus ou en dessous). */
export const ECART_DU_CHOIX = 6;

export interface EntreeDePlacement {
  /** La bulle du message qui porte la barre. */
  bulle: RectMesure;
  /** La zone visible : ce que les parents qui rognent laissent voir. */
  conteneur: RectMesure;
  choix: TailleDuChoix;
  /** Le bord de la bulle sur lequel s'aligner : `gauche` (message recu) ou `droite` (le sien). */
  cote: 'gauche' | 'droite';
  /** Le coin haut gauche de la barre, qui porte le choix en absolu : le resultat en est relatif. */
  origine: { left: number; top: number };
}

/** `valeur` ramenee dans [min, max] ; si la place manque (max < min), le minimum gagne : colle a gauche ou en haut. */
export function borner(valeur: number, min: number, max: number): number {
  return Math.max(min, Math.min(valeur, max));
}

/** La position du choix, relative a la barre : `left` et `top` a poser tels quels. */
export function placementDuChoixDeReaction({ bulle, conteneur, choix, cote, origine }: EntreeDePlacement): { left: number; top: number } {
  const voulueX = cote === 'gauche' ? bulle.left : bulle.right - choix.largeur;
  const minY = conteneur.top + MARGE_DU_CHOIX;
  const maxY = conteneur.bottom - MARGE_DU_CHOIX - choix.hauteur;
  const auDessus = bulle.top - ECART_DU_CHOIX - choix.hauteur;
  const enDessous = bulle.bottom + ECART_DU_CHOIX;
  // Au-dessus d'abord ; sinon en dessous s'il y a la place ; sinon au-dessus, ramene dans la zone.
  const voulueY = auDessus >= minY ? auDessus : enDessous <= maxY ? enDessous : auDessus;
  const x = borner(voulueX, conteneur.left + MARGE_DU_CHOIX, conteneur.right - MARGE_DU_CHOIX - choix.largeur);
  const y = borner(voulueY, minY, maxY);
  return { left: x - origine.left, top: y - origine.top };
}

/** Ce qui reste visible quand plusieurs parents rognent : l'intersection de leurs rectangles (null si elle est vide). */
export function intersection(rects: readonly RectMesure[]): RectMesure | null {
  if (rects.length === 0) return null;
  const zone = {
    left: Math.max(...rects.map(r => r.left)),
    top: Math.max(...rects.map(r => r.top)),
    right: Math.min(...rects.map(r => r.right)),
    bottom: Math.min(...rects.map(r => r.bottom)),
  };
  return zone.right > zone.left && zone.bottom > zone.top ? zone : null;
}
